import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CounterPartyType } from '@app/core/enums/counter-party.enum';
import { PartyRolesEnum } from '@app/core/enums/party-roles.enum';
import { CountryInterface } from '@app/core/interfaces';
import { CityStreetEnGbInterface } from '@app/core/interfaces/city-street.en-gb.interface';
import * as fromMyClaims from '@app/my-zone/pages/claims/store';
import * as fromClaims from '@app/my-zone/pages/claims/store/actions/claims.actions';
import * as fromInvolvedParties from '@app/my-zone/pages/claims/store/actions/involvedParties.actions';
import * as fromRoot from '@app/store/app.reducer';
import { AddressService, CityStreetRequestInterface } from '@app/store/my-zone/address/address.service';
import { CountrySelectorService } from '@app/store/my-zone/country/country-selector.service';
import * as countryActions from '@app/store/my-zone/country/country.actions';
import { select, Store } from '@ngrx/store';
import { NotificationInterface } from 'outshared-lib';
import { EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
    selector: 'ins-add-party-container',
    templateUrl: 'add-party.container.html',
})
export class AddPartyContainer implements OnInit, OnDestroy {
    @Input() role: string;
    @Input() fileSerialNumber: number;
    @Input() relationId: number;
    @Input() counterPartySerialNumber: number;
    @Input() addressRelationId: number;
    @Input() isCompanyDriver: boolean;

    @Output() readonly closed: EventEmitter<any> = new EventEmitter();

    public cityStreet?: CityStreetEnGbInterface;
    public claimErrors$: Observable<any> = this.store.pipe(select(fromMyClaims.getSelectedInvolvedPartiesErrors));
    public countries$: Observable<CountryInterface[]> = this.countrySelectorService.getCountries$;
    public notifications: NotificationInterface[];
    public hasAddressSuccess: boolean = false;
    public hasAddressError: boolean = false;

    private subscription = new Subscription();
    private addressChanges$ = new Subject();

    constructor(
        private store: Store<fromRoot.State>,
        private route: ActivatedRoute,
        private addressService: AddressService,
        private countrySelectorService: CountrySelectorService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        const claimErrorSubscription = this.claimErrors$.subscribe((errors) => (this.notifications = errors));
        this.store.dispatch(new countryActions.LoadCountries());

        const addressChangesSubscription = this.addressChanges$
            .pipe(
                debounceTime(500),
                distinctUntilChanged(),
                switchMap((address: CityStreetRequestInterface) => {
                    return this.getAddress(address).pipe(
                        catchError(() => {
                            this.handleAddressError();
                            return EMPTY;
                        }),
                    );
                }),
            )
            .subscribe((cityStreet: CityStreetEnGbInterface) => {
                this.handleAddressSuccess(cityStreet);
            });

        this.subscription.add(claimErrorSubscription);
        this.subscription.add(addressChangesSubscription);
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public onChangeAddress(address: CityStreetRequestInterface): void {
        this.hasAddressSuccess = false;
        this.hasAddressError = false;
        this.addressChanges$.next(address);
    }

    public onClearErrors(): void {
        this.store.dispatch(new fromInvolvedParties.ClearErrors(this.fileSerialNumber));
    }

    public onCancel(): void {
        this.closed.emit();
    }

    public onSubmit(payload: { party: any; counterPartyType?: CounterPartyType }): void {
        if (this.isCompanyDriver) {
            this.setDriverCounterParty(payload.party);
            return;
        }
        switch (this.role) {
            case PartyRolesEnum.Driver:
                this.setDriver(payload.party);
                break;
            case PartyRolesEnum.Witness:
                this.setWitness(payload.party);
                break;
            case PartyRolesEnum.Contact:
                this.setContact(payload.party);
                break;
            case PartyRolesEnum.CoInsured:
                this.setCoInsured(payload.party);
                break;
            case PartyRolesEnum.CounterParty:
                this.setCounterParty(payload.party, payload.counterPartyType);
                break;
        }
        this.onClearErrors();

        this.subscription.add(
            this.route.queryParams.subscribe((params) =>
                this.store.dispatch(new fromClaims.LoadActions({ dossier_volgnummer: params.d })),
            ),
        );

        this.closed.emit();
    }

    private handleAddressSuccess(cityStreet: CityStreetEnGbInterface): void {
        this.cityStreet = cityStreet;
        this.hasAddressSuccess = true;
        this.changeDetectorRef.detectChanges();
        this.store.dispatch(new fromInvolvedParties.ClearErrors(this.fileSerialNumber));
    }

    private handleAddressError(): void {
        this.cityStreet = null;
        this.hasAddressError = true;
        this.changeDetectorRef.detectChanges();
    }

    private setCounterParty(counterParty: any, counterPartyType?: CounterPartyType): void {
        const payload = {
            relatie_id: this.relationId,
            dossier_volgnummer: this.fileSerialNumber,
            persoon: counterParty,
            np_of_nnp_code: counterPartyType,
        };
        this.store.dispatch(new fromInvolvedParties.SetCounterParty(payload));
    }

    private setCoInsured(coInsured): void {
        const payload = {
            relatie_id: this.relationId,
            dossier_volgnummer: this.fileSerialNumber,
            persoon: coInsured,
        };
        this.store.dispatch(new fromInvolvedParties.SetCoInsured(payload));
    }

    private setContact(contact: any): void {
        const payload = {
            relatie_id: this.relationId,
            dossier_volgnummer: this.fileSerialNumber,
            persoon: contact,
        };
        this.store.dispatch(new fromInvolvedParties.SetContact(payload));
    }

    private setWitness(witness: any): void {
        const payload = {
            relatie_id: this.relationId,
            dossier_volgnummer: this.fileSerialNumber,
            persoon: witness,
        };
        this.store.dispatch(new fromInvolvedParties.SetWitness(payload));
    }

    private setDriver(driver: any): void {
        const payload = {
            dossier_volgnummer: this.fileSerialNumber,
            persoon: driver,
        };
        this.store.dispatch(new fromInvolvedParties.SetDriver(payload));
    }

    private setDriverCounterParty(driver: any): void {
        const payload = {
            relatie_id: this.relationId,
            dossier_volgnummer: this.fileSerialNumber,
            persoon: driver,
            tegenpartij_volgnummer: this.counterPartySerialNumber,
            adres_relatie_id: this.addressRelationId,
        };
        this.store.dispatch(new fromInvolvedParties.SetDriverCounterParty(payload));
    }

    private getAddress(address: CityStreetRequestInterface): Observable<CityStreetEnGbInterface> {
        return this.addressService.getCityStreet(address.postal_code, address.civic_number);
    }
}
